import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Typography,
  Popover,
  FormGroup,
  FormControlLabel,
  Checkbox,
  IconButton,
  Menu, MenuItem
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DataGrid } from '@mui/x-data-grid';
import ToggleButtonComponent from '../../../../shared/Dashboard/ToogleButton';
import { prepareColumnsAndGroups } from '../../../../shared/Helper';
import { Download } from 'lucide-react';
import CloseIcon from '@mui/icons-material/Close';
import { useAtom } from 'jotai';
import { fetchDownloadReportAtom } from '../../../../atoms/quality/QualityAtoms';
import { toLower } from 'lodash';

const TableComponent = ({
  open,
  onClose,
  type,
  setType,
  setVisibility,
  visibility,
  columns,
  data = {},
  list,
  setCurrentFilterValue,
  currentFilterValue,
  setPaginationModel,
  paginationModel,
  initialFilter,
  filters,
  setFilters,
  setList,
  setTableValue 
}) => {
  const { reports = [] } = data;
  const [currentFilter, setCurrentFilter] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [, setDownloadReportsAtom] = useAtom(fetchDownloadReportAtom);
  const [loading, setLoading] = useState(false);
  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);

  useEffect(() => {
    if (setTableValue && open) {
      setLoading(true);
      const currentValues = getResult(filters);
      const visibilityValue = visibility === 'internal' ? 0 : 1;

      const fetchData = async () => {
        try {
          if (filters?.length) {
            await setTableValue({
              ...currentValues,
              type: [toLower(type)],
              visibility: [visibilityValue],
              page: paginationModel?.page + 1,
              per_page: paginationModel?.pageSize,
            }, type.toLowerCase());
          } else {
            await setTableValue({
              visibility: [visibilityValue],
              type: [toLower(type)],
              page: paginationModel?.page + 1,
              per_page: paginationModel?.pageSize,
            }, type.toLowerCase());
          }
        } catch (error) {
          console.error('Error fetching data:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    }
  }, [type, visibility, filters, paginationModel, setTableValue, open]);

  const getResult = (data) => {
    return data?.reduce((acc, item) => ({ ...acc, ...item }), {});
  };

  const handleOpenFilter = (event, filterType) => {
    setAnchorEl(event.currentTarget);
    setCurrentFilter(filterType);
  };

  const handleDelete = (value) => {
    // console.log(value)
  }

  const handleImageClick = (url) => {
    // setSelectedImage(url);
    // setOpen(true);
  }

  const handleDownloadClick = (event) => {
    setDownloadAnchorEl(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setDownloadAnchorEl(null);
  };


  const handleDownloadAll = () => {
    try {
      setDownloadReportsAtom({
        visibility: [visibility === 'internal' ? 0 : 1],
      }, type.toLowerCase());
    } catch (err) {
      console.log(err);
    }
    handleDownloadClose();
  };

  const handleDownloadFiltered = () => {
    try {
      const currentValues = getResult(filters);
      const visibilityValue = visibility === 'internal' ? 0 : 1;

      setDownloadReportsAtom({
        ...currentValues,
        visibility: [visibilityValue],
      }, type.toLowerCase());
    } catch (err) {
      console.log(err);
    }
    handleDownloadClose();
  };

  const { visibleColumns, columnGroupingModel } = prepareColumnsAndGroups(columns, handleOpenFilter, filters, handleImageClick, handleDelete);

  const handleCloseFilter = () => {
    setAnchorEl(null);
  };

  const handleSelectAll = (filterType, isChecked) => {
    setFilters((prevFilters) => {
      const index = prevFilters.findIndex(f => f.hasOwnProperty(filterType));

      if (index !== -1) {
        return prevFilters.filter((_, i) => i !== index);
      } else if (isChecked) {
        const data = {};
        data[filterType] = initialFilter[filterType];
        return [...prevFilters, data];
      } else {
        return prevFilters;
      }
    });

    setCurrentFilterValue((prevFilters) => ({
      ...prevFilters,
      [filterType]: isChecked ? [...list[filterType]] : [],
    }));
    setAnchorEl(null);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters((prevFilters) => {
      let sourceArray;

      if (prevFilters.length === 0) {
        sourceArray = [];
      } else {
        sourceArray = currentFilterValue?.[filterType] || [];
      }

      const isSelected = sourceArray.includes(value);
      const newValues = isSelected
        ? sourceArray.filter((item) => item !== value)
        : [...sourceArray, value];

      const updatedFilters = [...prevFilters];
      const index = updatedFilters.findIndex((f) => f.hasOwnProperty(filterType));

      if (index !== -1) {
        updatedFilters[index] = { [filterType]: newValues };
      } else {
        updatedFilters.push({ [filterType]: newValues });
      }

      return updatedFilters;
    });

    setAnchorEl(null);
  };

  const handleClearFilter = (filterType) => {
    if (filters?.length === 1 && filters?.find(obj => obj.hasOwnProperty(filterType))) {
      setFilters([])
    } else {
      setFilters((prevFilters) => {
        const targetFilter = prevFilters.find(obj => obj.hasOwnProperty(filterType));

        if (!targetFilter || !targetFilter[filterType]?.length) {
          return [];
        }

        const updatedFilters = prevFilters
          .filter(obj => !obj.hasOwnProperty(filterType));

        return [...updatedFilters, { [filterType]: [] }];
      });
    }

    setAnchorEl(null);
  };

  const handleClearFilters = () => {
    setCurrentFilterValue(
      {
        block: [],
        row: [],
        scope: [],
        section: [],
      }
    )
    setFilters([])
    setList(
      {
        block: [],
        row: [],
        scope: [],
        section: [],
      }
    )
  }


  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0
        }}>
          <Typography fontWeight={600}>ITC/ITR Report</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box mb={2} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <ToggleButtonComponent
              data={["ITC", "ITR"]}
              active={type}
              setActive={setType}
            />
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <ToggleButtonComponent
              data={["external", "internal"]}
              active={visibility}
              setActive={setVisibility}
            />
            <Button
              variant="outlined"
              startIcon={<Download size={16} />}
              endIcon={<ExpandMoreIcon />}
              onClick={handleDownloadClick}
            >
              Download
            </Button>
            <Menu
              anchorEl={downloadAnchorEl}
              open={Boolean(downloadAnchorEl)}
              onClose={handleDownloadClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              <MenuItem onClick={handleDownloadAll}>Download All</MenuItem>
              <MenuItem onClick={handleDownloadFiltered}>Download Filtered Data</MenuItem>
            </Menu>

            <Button variant={"outlined"} onClick={handleClearFilters}>Clear Filters</Button>
          </Box>
        </Box>

        <Box sx={{ height: 400, width: '100%' }}>
          <DataGrid
            rows={reports?.map((row) => ({ ...row, id: row.id }))}
            columns={visibleColumns}
            pageSizeOptions={[10, 20, 50]}
            disableRowSelectionOnClick
            disableColumnMenu
            disableSelectionOnClick
            columnGroupingModel={columnGroupingModel}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            loading={loading}
            sx={{
              '& .MuiDataGrid-columnHeader[data-field="block"]': {
                backgroundColor: filters?.find((item) => Object.keys(item).includes("block"))?.block?.length > 0 ? '#f16352' : '#f5f5f5',
                color: filters?.find((item) => Object.keys(item).includes("block"))?.block?.length > 0 ? 'white' : '',
              },
              '& .MuiDataGrid-columnHeader[data-field="row"]': {
                backgroundColor: filters?.find((item) => Object.keys(item).includes("row"))?.row?.length > 0 ? '#f16352' : '#f5f5f5',
                color: filters?.find((item) => Object.keys(item).includes("row"))?.row?.length > 0 ? 'white' : '',
              },
              '& .MuiDataGrid-columnHeader[data-field="scope"]': {
                backgroundColor: filters?.find((item) => Object.keys(item).includes("scope"))?.scope?.length > 0 ? '#f16352' : '#f5f5f5',
                color: filters?.find((item) => Object.keys(item).includes("scope"))?.scope?.length > 0 ? 'white' : '',
              },
              '& .MuiDataGrid-columnHeader[data-field="section"]': {
                backgroundColor: filters?.find((item) => Object.keys(item).includes("section"))?.section?.length > 0 ? '#f16352' : '#f5f5f5',
                color: filters?.find((item) => Object.keys(item).includes("section"))?.section?.length > 0 ? 'white' : '',
              },
            }}
          />
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleCloseFilter}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            PaperProps={{
              sx: {
                width: 230,
                height: 300,
                overflowY: 'auto',
                overflowX: 'hidden',
                padding: 2
              },
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <div className="font-medium" style={{ marginTop: 0, fontWeight: 600 }}>Filter</div>
              <div
                onClick={() => handleClearFilter(currentFilter)}
                style={{ cursor: "pointer", color: "blue", fontWeight: 500, fontSize: 16 }}
              >
                Clear
              </div>
            </div>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={
                      (list?.[currentFilter]?.length > 0 &&
                        list?.[currentFilter]?.every((value) =>
                          currentFilterValue?.[currentFilter]?.includes(value)
                        )) || false
                    }
                    indeterminate={
                      (list?.[currentFilter]?.some((value) =>
                        currentFilterValue?.[currentFilter]?.includes(value)
                      ) &&
                        !list?.[currentFilter]?.every((value) =>
                          currentFilterValue?.[currentFilter]?.includes(value)
                        )) || false
                    }
                    onChange={(e) => handleSelectAll(currentFilter, e.target.checked)}
                  />
                }
                label={
                  <Box fontSize={16}>
                    {(list?.[currentFilter]?.length > 0 &&
                      list?.[currentFilter]?.every((value) =>
                        currentFilterValue?.[currentFilter]?.includes(value)
                      )) ? "Unselect All" : "Select All"}
                  </Box>
                }
              />
              {(list?.[currentFilter]?.length ? list?.[currentFilter] : []).map((value) => (
                <FormControlLabel
                  key={value}
                  control={
                    <Checkbox
                      size='small'
                      checked={currentFilterValue?.[currentFilter]?.includes(value) || false}
                      onChange={() => handleFilterChange(currentFilter, value)}
                    />
                  }
                  label={<Box fontSize={16}>{value}</Box>}
                />
              ))}
            </FormGroup>
          </Popover>
        </Box>
      </DialogContent>
    </Dialog >
  );
};

export default TableComponent;